// src/app/services/chat.service.ts
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReferenceDocument } from '@coin/chat-bot-util-types';
import { AuthService } from '@coin/shared/auth/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { Observable, Subject } from 'rxjs';

export interface ResponseChatMessage {
  name: string;
  data: string;
  id: string | number;
  referenceDocs: Array<ReferenceDocument>;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private _baseUrl = environment.chatBot.baseUrl;
  private _apiUrl = environment.chatBot.apiUrl;
  private _userService = inject(AuthService);

  streamChat(userInput: string): Observable<ResponseChatMessage> {
    const output$ = new Subject<ResponseChatMessage>();
    const body = JSON.stringify({ user_input: userInput });

    (async () => {
      let token = '';
      try {
        token = await this._userService.getIdToken();
        if (!token) {
          output$.error('No bearer token found');
          return;
        }
      } catch (error) {
        output$.error('Error fetching token');
        return;
      }

      try {
        const sha256 = await this.computeSHA256(body);

        const response = await fetch(this._apiUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-amz-content-sha256': sha256
          },
          body
        });

        if (!response.ok || !response.body) {
          output$.error(`HTTP Error: ${response.status}`);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = ''; // Buffer to accumulate incomplete JSON
        const flag = true;
        while (flag) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          if (chunk.trim() === '') continue;

          // Add chunk to buffer
          buffer += chunk;

          // Process complete JSON objects from buffer
          const { processedMessages, remainingBuffer } = this.processJsonBuffer(buffer);
          buffer = remainingBuffer;

          // Emit processed messages
          processedMessages.forEach(message => {
            if (this.isValidResponseMessage(message)) {
              output$.next(message);
            }
          });
        }

        // Process any remaining buffer content
        if (buffer.trim()) {
          const { processedMessages } = this.processJsonBuffer(buffer, true);
          processedMessages.forEach(message => {
            if (this.isValidResponseMessage(message)) {
              output$.next(message);
            }
          });
        }

        output$.complete();
      } catch (error) {
        console.error('Error during fetch or stream processing:', error);
        output$.error(error);
      }
    })();

    return output$.asObservable();
  }

  /**
   * Process JSON buffer and extract complete JSON objects
   */
  private processJsonBuffer(
    buffer: string,
    forceProcess = false
  ): {
    processedMessages: ResponseChatMessage[];
    remainingBuffer: string;
  } {
    const processedMessages: ResponseChatMessage[] = [];
    let remainingBuffer = buffer;

    // Find complete JSON objects in the buffer
    let startIndex = 0;

    while (startIndex < remainingBuffer.length) {
      const openBraceIndex = remainingBuffer.indexOf('{', startIndex);
      if (openBraceIndex === -1) break;

      const endIndex = this.findJsonBoundary(remainingBuffer.substring(openBraceIndex));

      if (endIndex === -1) {
        // No complete JSON found, keep remaining buffer
        if (forceProcess) {
          // Try to parse what we have if forced
          const partialJson = remainingBuffer.substring(openBraceIndex);
          const message = this.tryParsePartialJson(partialJson);
          if (message) {
            processedMessages.push(message);
          }
          remainingBuffer = '';
        } else {
          remainingBuffer = remainingBuffer.substring(openBraceIndex);
        }
        break;
      }

      // Extract complete JSON
      const jsonStr = remainingBuffer.substring(openBraceIndex, openBraceIndex + endIndex + 1);
      const message = this.parseJsonString(jsonStr);

      if (message) {
        processedMessages.push(message);
      }

      startIndex = openBraceIndex + endIndex + 1;
      remainingBuffer = remainingBuffer.substring(startIndex);
      startIndex = 0;
    }

    return { processedMessages, remainingBuffer };
  }

  /**
   * Attempts to find the end index of a complete JSON object within a string.
   *
   * This function parses the input string `text` character by character,
   * tracking nesting depth of `{}` brackets while accounting for quoted strings
   * and escaped characters.
   *
   * Purpose:
   * --------
   * This is typically used in scenarios where you receive a stream of text
   * that may contain one or more JSON objects, and you want to extract the
   * boundary of the **first complete JSON object**.
   *
   * Example use cases:
   * - Parsing streamed logs or API responses
   * - Splitting multiple concatenated JSON objects
   * - Detecting the end of a JSON object in loosely formatted input
   *
   * @param text - A string that potentially contains JSON content.
   * @returns The index (0-based) of the last character (`}`) of the first
   *          complete top-level JSON object if found, or `-1` if incomplete.
   */
  private findJsonBoundary(text: string): number {
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (char === '\\') {
          escaped = true;
        } else if (char === '"') {
          inString = false;
        }
      } else if (char === '"') {
        inString = true;
      } else if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
        if (depth === 0) {
          return i; // Found the end of a complete object
        }
      }
    }

    return -1; // No complete object found
  }

  /**
   * Parse a complete JSON string into ResponseChatMessage
   */
  private parseJsonString(jsonStr: string): ResponseChatMessage | null {
    try {
      const parsed = JSON.parse(jsonStr.trim());

      if (this.isValidParsedObject(parsed)) {
        return {
          name: parsed.name || 'message',
          data: parsed.data || '',
          id: parsed.id || Date.now(),
          referenceDocs: parsed.referenceDocs || []
        };
      }
    } catch (error) {
      console.warn('Failed to parse JSON:', jsonStr, error);
    }

    return null;
  }

  /**
   * Try to parse partial/incomplete JSON by attempting to complete it
   */
  private tryParsePartialJson(partialJson: string): ResponseChatMessage | null {
    // Try to complete common incomplete patterns
    const completionAttempts = [
      `${partialJson}}`,
      `${partialJson}]}`,
      `${partialJson}"]}`,
      `${partialJson}","referenceDocs":[]}`
    ];

    for (const attempt of completionAttempts) {
      try {
        const parsed = JSON.parse(attempt);
        if (this.isValidParsedObject(parsed)) {
          return {
            name: parsed.name || 'message',
            data: parsed.data || '',
            id: parsed.id || Date.now(),
            referenceDocs: parsed.referenceDocs || []
          };
        }
      } catch {
        // Continue to next attempt
      }
    }

    console.warn('Could not parse partial JSON:', partialJson);
    return null;
  }

  /**
   * Validate if parsed object has required structure
   */
  private isValidParsedObject(obj: ResponseChatMessage): boolean {
    return obj && typeof obj === 'object' && typeof obj.name === 'string' && Array.isArray(obj.referenceDocs);
  }

  /**
   * Validate if message is a valid ResponseChatMessage
   */
  private isValidResponseMessage(message: ResponseChatMessage): boolean {
    return (
      message &&
      typeof message.name === 'string' &&
      typeof message.data === 'string' &&
      Array.isArray(message.referenceDocs)
    );
  }

  /**
   * Compute SHA-256 hash of a given string, as a hex digest.
   */
  private async computeSHA256(message: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  _downloadFile(document: ReferenceDocument) {
    try {
      // Get auth token

      const headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });

      // Download as blob
      this.http
        .get<{
          success: boolean;
          downloadUrl: string;
          expiresIn: number;
          fileName: undefined;
          timestamp: string;
        }>(`${this._baseUrl}${document.downloadEndpoint}`, {
          headers,
          responseType: 'json',
          observe: 'response'
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          if (event.body && event.body.success) {
            const { downloadUrl } = event.body;
            const filename = document.fileName || 'downloaded_file';
            this.saveBlob(downloadUrl, filename);
          } else {
            console.error('Download failed:', event.body);
          }
        });
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  private saveBlob(path: string, filename: string): void {
    const link = document.createElement('a');
    // open blank
    link.target = '_blank';
    link.href = path;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(path);
  }

  /**
   * Download with progress tracking
   */
  downloadReferenceDocument(doc: ReferenceDocument) {
    return this._downloadFile(doc);
  }
}
